// 도착정보 응답 -> 화면에 그릴 행. 제공자가 둘이고 **응답 모양이 완전히 다르다.**
//
// **왜 별도 모듈인가.** 두 응답 다 빈 값과 타입이 지저분해서(아래) 변환이 패널
// 안에 있으면 브라우저에서만 확인할 수 있다. 여기 두면 node 로 바로 검사한다
// (`tools/test-bus-arrival.mjs`).
//
// 공통 행 모양:
//   {route, buses: [{text, sub, notes, sort}]}
//     text  주 표시 ('3분' · '곧 도착' · '출발대기' · '운행종료')
//     sub   부 표시 ('3번째 전') 또는 ''
//     notes 차량번호·저상·혼잡도 등 있으면
//     sort  정렬용 초. 모르면 LAST (뒤로 밀린다)
//
// ⚠️ **정렬 키에 Infinity 를 쓰면 안 된다.** 비교 함수가 뺄셈이라
// `Infinity - Infinity = NaN` 이 되고, 그러면 Array#sort 가 순서를 보장하지
// 않는다 - 예정 차량이 없는 노선이 여러 개면 순서가 무작위가 된다.
const LAST = Number.MAX_SAFE_INTEGER;

const GBIS_CROWDED = ['', '여유', '보통', '혼잡', '매우혼잡'];
// 서울은 4단계이고 코드가 3부터 시작한다 (경기는 1부터 3단계다).
// providers.py 의 SEOUL_OCC·GBIS_OCC 와 같은 규약이다.
const SEOUL_CROWDED = {3: '여유', 4: '보통', 5: '혼잡', 6: '매우혼잡'};

function notesOf(parts) {
    return parts.filter(Boolean).join(' · ');
}

// ── 경기 GBIS (getBusArrivalListv2) ───────────────────────────────────────
// 실측(2026-09-07)으로 확인한 함정 세 가지:
//   1. `flag` 로 '차량 없음' 을 가릴 수 없다. 도착 3분 남은 5-3 도 `PASS` 였다.
//      비어 있는지는 **`predictTime1` 이 빈 문자열인지**로 본다.
//   2. `routeName` 이 문자열이 아닐 수 있다. 숫자 노선(721·99)은 JSON number 로
//      온다 - 그대로 innerHTML 에 넣으면 되지만 `.trim()` 을 부르면 터진다.
//   3. 노선이 하나면 `busArrivalList` 가 배열이 아니라 객체로 온다.
function gbisBus(item, n) {
    const minutes = item[`predictTime${n}`];

    // 빈 문자열 = 그 순번의 예정 차량이 없다. 0 은 '곧 도착' 이라 유효값이다.
    if (minutes === '' || minutes === null || minutes === undefined) {
        return;
    }

    const stops = +item[`locationNo${n}`] || 0,
        // ⚠️ 잔여석은 **0 을 신뢰하지 않는다.** 좌석 정보를 안 주는 일반버스도
        // -1 이 아니라 0 으로 오는 걸 봤다 (5-3, 일반). 0 을 그대로 찍으면
        // '만석' 으로 읽힌다. 좌석버스의 진짜 0 은 같이 버려진다 - 잘못된
        // 정보를 보여주는 쪽이 더 나쁘다.
        seats = +item[`remainSeatCnt${n}`] > 0 ? +item[`remainSeatCnt${n}`] : 0;

    return {
        text: +minutes < 1 ? '곧 도착' : `${+minutes}분`,
        sub: `${stops}번째 전`,
        notes: notesOf([
            String(item[`plateNo${n}`] || ''),
            +item[`lowPlate${n}`] === 1 ? '저상' : '',
            GBIS_CROWDED[+item[`crowded${n}`]] || '',
            seats ? `좌석 ${seats}` : ''
        ]),
        sort: +item[`predictTimeSec${n}`] || +minutes * 60 || 0
    };
}

function parseGbis(json) {
    const list = ((json.response || {}).msgBody || {}).busArrivalList;

    if (!list) {
        return [];
    }
    return (Array.isArray(list) ? list : [list]).map(item => ({
        route: String(item.routeName === undefined ? '' : item.routeName),
        buses: [gbisBus(item, 1), gbisBus(item, 2)].filter(Boolean)
    }));
}

// ── 서울 (stationinfo/getStationByUid) ────────────────────────────────────
// 실측(2026-09-07)으로 확인한 차이:
//   1. **`plainNo`(차량번호)가 null 이다.** 서울은 안 준다 (GBIS 는 plateNo 를 준다).
//   2. `arrmsg1` 이 전광판 문구 그대로다 - '7분후[4번째 전]' · '곧 도착' ·
//      '[차고지출발] ' · '출발대기' · '운행종료'. 문구가 이렇게 여러 갈래라
//      숫자(`traTime`)로 재구성하면 상태를 전부 '0분' 으로 뭉갠다. 그래서
//      **문구를 1차 표시로 쓰고** 숫자는 정렬에만 쓴다.
//   3. `congestion` 은 대개 '0'(미제공)이고 `busType` 도 '0' 이 대부분이다.
//      없는 걸 '일반·여유' 로 단정하지 않고 그냥 비운다.
//
// ⚠️ **'대괄호가 있으면 예측' 이 아니다.** 실측: '곧 도착' 은 대괄호가 없는데
// traTime 39 로 진짜 임박 도착이고, 반대로 '[차고지출발] ' 은 메시지 전체가
// 대괄호다(traTime 28, 뒤에 공백까지 붙는다). 그래서 **예측 여부는 traTime 으로
// 가른다** - 대괄호는 표시를 쪼개는 데만 쓴다. 처음엔 대괄호로 갈라서 '곧 도착'
// 이 정렬 뒤로 밀렸다 (라이브 응답을 파서에 직접 먹여서 찾았다 - 화면에서는
// 그 정류장에 마침 예측이 없어 증상이 안 보였다).
const SEOUL_MSG = /^(.+?)\[(.+?)\]\s*$/;

function seoulBus(item, n) {
    const msg = String(item[`arrmsg${n}`] || '').trim();

    if (!msg) {
        return;
    }

    const m = SEOUL_MSG.exec(msg),
        secs = +item[`traTime${n}`];

    return {
        text: (m ? m[1] : msg).replace(/후$/, '').trim(),
        sub: m ? m[2].trim() : '',
        notes: notesOf([
            +item[`busType${n}`] === 1 ? '저상' : '',
            SEOUL_CROWDED[+item[`congestion${n}`]] || '',
            item[`isLast${n}`] === '1' ? '막차' : '',
            item[`isFullFlag${n}`] === '1' ? '만차' : ''
        ]),
        // 예측이 아닌 상태('출발대기'·'운행종료')는 traTime 이 0 이다. 그걸 0초로
        // 정렬하면 곧 오는 버스보다 앞에 선다.
        sort: secs > 0 ? secs : LAST
    };
}

function parseSeoul(json) {
    const list = (json.msgBody || {}).itemList;

    if (!list) {
        return [];
    }
    return (Array.isArray(list) ? list : [list]).map(item => ({
        route: String(item.busRouteAbrv || item.rtNm || ''),
        buses: [seoulBus(item, 1), seoulBus(item, 2)].filter(Boolean)
    }));
}

const PARSERS = {gbis: parseGbis, seoul: parseSeoul};

/**
 * 도착정보 응답을 노선 행으로 바꾼다.
 * @param {string} src - 제공자 (`gbis` | `seoul`)
 * @param {Object} json - `/api/arrival/<src>` 응답(제공자 원본)
 * @returns {Array<Object>} 도착 임박순 노선 행. 예정 차량이 없는 노선은 뒤로 민다
 */
export function parseArrivals(src, json) {
    const parser = PARSERS[src];

    if (!parser || !json) {
        return [];
    }
    return parser(json).sort((a, b) =>
        (a.buses.length ? a.buses[0].sort : LAST) -
        (b.buses.length ? b.buses[0].sort : LAST)
    );
}

/**
 * 응답이 정상인지. 두 제공자 다 HTTP 200 에 자체 코드로 실패를 알린다.
 * @param {string} src - 제공자 (`gbis` | `seoul`)
 * @param {Object} json - `/api/arrival/<src>` 응답
 * @returns {boolean} True if the response is a normal one
 */
export function isNormal(src, json) {
    if (!json) {
        return false;
    }
    if (src === 'seoul') {
        // 서울은 headerCd 0 이 정상이다. 4 는 '결과 없음' 이라 오류가 아니지만
        // 표시할 것도 없으므로 정상으로 통과시키고 빈 목록이 되게 둔다.
        const cd = ((json.msgHeader || {}).headerCd);

        return cd === '0' || cd === 0 || cd === '4' || cd === 4;
    }
    return +(((json.response || {}).msgHeader || {}).resultCode) === 0;
}
